import React, { useState, useMemo, useRef } from 'react'
import { X, Loader2 } from 'lucide-react'
import { usePlayerStats, usePlayerStatsByName, usePlayerRanking } from '../hooks/useGames'
import TeamLogo from './TeamLogo'

// Detect NFL position from stats or position string
function detectNFLPosition(position, seasonAvg) {
  const pos = (position || '').toUpperCase()
  
  // Explicit position check
  if (pos.includes('QB') || pos.includes('QUARTERBACK')) return 'QB'
  if (pos.includes('RB') || pos.includes('RUNNING')) return 'RB'
  if (pos.includes('WR') || pos.includes('WIDE')) return 'WR'
  if (pos.includes('TE') || pos.includes('TIGHT')) return 'TE'
  
  // Infer from stats if position not explicit
  if (seasonAvg) {
    const passYds = seasonAvg.passing_yards || seasonAvg.pass_yds || 0
    const rushYds = seasonAvg.rushing_yards || seasonAvg.rush_yds || 0
    const recYds = seasonAvg.receiving_yards || seasonAvg.rec_yds || 0
    const recs = seasonAvg.receptions || seasonAvg.rec || 0
    
    if (passYds > 100) return 'QB'
    if (rushYds > 30 && recs < 3) return 'RB'
    if (recYds > 20 || recs > 2) return 'WR'
  }
  
  return 'WR' // Default to WR
}

// Status badge colors and labels
const STATUS_CONFIG = {
  OUT: { bg: 'bg-red-100', text: 'text-red-700', label: 'OUT' },
  DTD: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'DTD' },
  GTD: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'GTD' },
  DBT: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'DOUBTFUL' },
  PROB: { bg: 'bg-green-100', text: 'text-green-700', label: 'PROBABLE' },
  IN: { bg: 'bg-green-100', text: 'text-green-700', label: 'ACTIVE' },
}

// Compute last N games vs line stats
function computeVsLine(recentGames, line, statKey = 'pts') {
  if (!recentGames?.length || !line) return null
  
  const games = recentGames.slice(0, 5)
  const hits = games.filter(g => (g?.[statKey] ?? 0) >= line).length
  const avg = games.reduce((sum, g) => sum + (g?.[statKey] ?? 0), 0) / games.length
  const diff = avg - line
  
  return {
    hits,
    total: games.length,
    avg: avg.toFixed(1),
    diff: diff.toFixed(1),
    diffPositive: diff >= 0,
    hitRate: `${hits}/${games.length}`,
  }
}

// Format opponent string
function formatOpponent(game, playerTeamId) {
  if (!game?.game) return { opp: '--', isHome: false, date: '' }
  
  const homeTeam = game.game.home_team?.abbreviation || ''
  const visitorTeam = game.game.visitor_team?.abbreviation || ''
  const isHome = game.game.home_team?.id === playerTeamId
  const opp = isHome ? visitorTeam : homeTeam
  const prefix = isHome ? 'vs' : '@'
  
  let date = ''
  if (game.game.date) {
    // Parse date string directly to avoid timezone shift
    // game.game.date is "YYYY-MM-DD" (sports calendar date)
    const [year, month, day] = game.game.date.split('-')
    date = `${parseInt(month)}/${parseInt(day)}`
  }
  
  return { opp: `${prefix} ${opp}`, isHome, date }
}

// Check if a game is a DNP (Did Not Play)
function isDNP(game) {
  const min = typeof game?.min === 'string' ? parseInt(game.min.split(':')[0]) || 0 : game?.min || 0
  return min === 0 && (game?.pts || 0) === 0 && (game?.reb || 0) === 0 && (game?.ast || 0) === 0
}

// Game Log Tab Component - Minimal stats table matching boxscore style
function GameLogTab({ recentGames, playerTeamId, playerName, onAskAI, onClose, league = 'nba' }) {
  if (!recentGames?.length) {
    return (
      <div className="text-center py-16 px-5">
        <p className="text-sm text-gray-400">No recent games</p>
      </div>
    )
  }
  
  const isNFL = league?.toLowerCase() === 'nfl'
  
  // Filter out DNPs for averages calculation
  const playedGames = recentGames.filter(g => !isDNP(g))
  const gamesForAvg = playedGames.length || 1 // Avoid division by zero
  
  // Calculate averages (excluding DNPs) - different for NFL
  const avgPts = isNFL ? null : (playedGames.reduce((sum, g) => sum + (g?.pts || 0), 0) / gamesForAvg).toFixed(1)
  const avgReb = isNFL ? null : (playedGames.reduce((sum, g) => sum + (g?.reb || 0), 0) / gamesForAvg).toFixed(1)
  const avgAst = isNFL ? null : (playedGames.reduce((sum, g) => sum + (g?.ast || 0), 0) / gamesForAvg).toFixed(1)
  const avg3pm = isNFL ? null : (playedGames.reduce((sum, g) => sum + (g?.fg3m || 0), 0) / gamesForAvg).toFixed(1)
  
  // NFL averages
  const avgPassYds = isNFL ? (playedGames.reduce((sum, g) => sum + (g?.pass_yds || 0), 0) / gamesForAvg).toFixed(0) : null
  const avgRushYds = isNFL ? (playedGames.reduce((sum, g) => sum + (g?.rush_yds || 0), 0) / gamesForAvg).toFixed(0) : null
  const avgRecYds = isNFL ? (playedGames.reduce((sum, g) => sum + (g?.rec_yds || 0), 0) / gamesForAvg).toFixed(0) : null
  const avgTotalTd = isNFL ? (playedGames.reduce((sum, g) => sum + (g?.total_td || 0), 0) / gamesForAvg).toFixed(1) : null
  
  const handleGameClick = (game) => {
    const { date } = formatOpponent(game, playerTeamId)
    const prompt = `Recap ${playerName}'s game on ${date}. How did they perform?`
    onAskAI?.(prompt)
    onClose?.()
  }
  
  return (
    <div className="px-3 py-3">
      {/* Averages Header - Minimal */}
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
          Last {recentGames.length}{playedGames.length < recentGames.length ? ` (${playedGames.length} GP)` : ''}
        </p>
        {isNFL ? (
          <div className="flex items-center gap-3 text-[10px] text-gray-500">
            {avgPassYds > 0 && <span>{avgPassYds} <span className="text-gray-400">PASS</span></span>}
            {avgRushYds > 0 && <span>{avgRushYds} <span className="text-gray-400">RUSH</span></span>}
            {avgRecYds > 0 && <span>{avgRecYds} <span className="text-gray-400">REC</span></span>}
            <span>{avgTotalTd} <span className="text-gray-400">TD</span></span>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-[10px] text-gray-500">
            <span>{avgPts} <span className="text-gray-400">PPG</span></span>
            <span>{avgReb} <span className="text-gray-400">RPG</span></span>
            <span>{avgAst} <span className="text-gray-400">APG</span></span>
            <span>{avg3pm} <span className="text-gray-400">3PM</span></span>
          </div>
        )}
      </div>
      
      {/* Table - Horizontal scroll for narrow screens */}
      <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {isNFL ? (
          /* NFL Game Log Table */
          <table className="w-full text-[11px]" style={{ minWidth: '340px' }}>
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 px-2 font-medium text-gray-400 w-16">Date</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-10">PASS</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-10">RUSH</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-10">REC</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-8">TD</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-8">INT</th>
              </tr>
            </thead>
            <tbody>
              {recentGames.map((game, i) => {
                const opponent = game?.opponent || 'OPP'
                const date = game?.date ? new Date(game.date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : ''
                const hasPassYds = (game?.pass_yds || 0) > 0
                const hasRushYds = (game?.rush_yds || 0) > 0
                const hasRecYds = (game?.rec_yds || 0) > 0
                
                return (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer">
                    <td className="py-2 px-2">
                      <p className="font-medium text-gray-800">{date}</p>
                      <p className="text-[9px] text-gray-400">{game?.home ? 'vs' : '@'} {opponent}</p>
                    </td>
                    <td className={`text-right py-2 px-1.5 ${hasPassYds ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
                      {hasPassYds ? game.pass_yds : '-'}
                    </td>
                    <td className={`text-right py-2 px-1.5 ${hasRushYds ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
                      {hasRushYds ? game.rush_yds : '-'}
                    </td>
                    <td className={`text-right py-2 px-1.5 ${hasRecYds ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
                      {hasRecYds ? game.rec_yds : '-'}
                    </td>
                    <td className={`text-right py-2 px-1.5 ${(game?.total_td || 0) > 0 ? 'font-semibold text-pink-600' : 'text-gray-400'}`}>
                      {game?.total_td || 0}
                    </td>
                    <td className="text-right py-2 px-1.5 text-gray-500">{game?.int || 0}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : (
          /* NBA Game Log Table */
          <table className="w-full text-[11px]" style={{ minWidth: '340px' }}>
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 px-2 font-medium text-gray-400 w-16">Date</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-8">MIN</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-8">PTS</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-10">FG</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-8">3PM</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-8">REB</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-8">AST</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-8">STL</th>
                <th className="text-right py-2 px-1.5 font-medium text-gray-400 w-8">BLK</th>
              </tr>
            </thead>
          <tbody>
            {recentGames.map((game, i) => {
              const { opp, date } = formatOpponent(game, playerTeamId)
              const min = typeof game?.min === 'string' ? game.min.split(':')[0] : game?.min || 0
              const isPtsHigh = game?.pts >= 25
              const gameIsDNP = isDNP(game)
              
              return (
                <tr 
                  key={i}
                  onClick={() => !gameIsDNP && handleGameClick(game)}
                  className={`border-b border-gray-50 transition-colors ${gameIsDNP ? 'opacity-50' : 'hover:bg-gray-50 cursor-pointer'}`}
                >
                  <td className="py-2 px-2">
                    <p className="font-medium text-gray-800">{date}</p>
                    <p className="text-[9px] text-gray-400">{opp}</p>
                  </td>
                  {gameIsDNP ? (
                    <td colSpan={8} className="text-center py-2 px-1.5 text-gray-400 text-[10px] font-medium">
                      DNP
                    </td>
                  ) : (
                    <>
                      <td className="text-right py-2 px-1.5 text-gray-500">{min}</td>
                      <td className={`text-right py-2 px-1.5 font-semibold ${isPtsHigh ? 'text-pink-600' : 'text-gray-900'}`}>
                        {game?.pts || 0}
                      </td>
                      <td className="text-right py-2 px-1.5 text-gray-500">{game?.fgm || 0}-{game?.fga || 0}</td>
                      <td className="text-right py-2 px-1.5 text-gray-600">{game?.fg3m || 0}</td>
                      <td className="text-right py-2 px-1.5 text-gray-700">{game?.reb || 0}</td>
                      <td className="text-right py-2 px-1.5 text-gray-700">{game?.ast || 0}</td>
                      <td className="text-right py-2 px-1.5 text-gray-500">{game?.stl || 0}</td>
                      <td className="text-right py-2 px-1.5 text-gray-500">{game?.blk || 0}</td>
                    </>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
        )}
      </div>
      
      {/* Tap hint */}
      <p className="text-center text-[9px] text-gray-400 mt-2">
        Tap a game to ask about it
      </p>
    </div>
  )
}

// ESPN-style Player Card v2 with swipeable tabs
export default function PlayerCardV2({ player, onClose, onAskAI, gameContext, injury, props: playerProps }) {
  const [selectedProp, setSelectedProp] = useState('pts')
  const [selectedBook, setSelectedBook] = useState('dk') // DK or FD toggle
  const [activeTab, setActiveTab] = useState('profile') // 'profile' or 'gamelog'
  
  // Swipe handling
  const touchStartX = useRef(null)
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX }
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return
    const diff = touchStartX.current - e.changedTouches[0].clientX
    if (Math.abs(diff) > 50) {
      setActiveTab(diff > 0 ? 'gamelog' : 'profile')
    }
    touchStartX.current = null
  }
  
  // Player info - get name and team first for fallback search
  const playerName = player?.name || `${player?.player?.first_name || ''} ${player?.player?.last_name || ''}`.trim() || 'Player'
  const playerTeamHint = player?.team?.abbreviation || player?.team || '' // For disambiguation
  
  // Fetch player stats by ID first - use league from gameContext
  const playerId = player?.player?.id || player?.id
  const playerLeague = gameContext?.league || 'nba'
  const { data: playerDataById, isLoading: loadingById } = usePlayerStats(playerId, playerLeague)
  
  // Fallback: search by name + team if ID returns no data or error
  const needsNameSearch = !loadingById && (!playerDataById?.recentGames?.length && !playerDataById?.seasonAverages)
  const { data: playerDataByName, isLoading: loadingByName } = usePlayerStatsByName(
    needsNameSearch ? playerName : null,
    needsNameSearch ? playerTeamHint : null
  )
  
  // Use whichever data source worked
  const playerData = playerDataById?.recentGames?.length ? playerDataById : playerDataByName
  const isLoading = loadingById || (needsNameSearch && loadingByName)
  const resolvedPlayerId = playerData?.playerId || playerId
  
  const { data: rankingData } = usePlayerRanking(resolvedPlayerId, 'pts')
  
  const seasonAvg = playerData?.seasonAverages
  const recentGames = playerData?.recentGames?.slice(0, 10) || []
  const ptsRank = rankingData?.rank // e.g., 3 for 3rd in points
  // Use canonical resolved fields from API (NFL) or fallback to player object
  const teamAbbr = playerData?.teamAbbr || player?.team?.abbreviation || player?.team || recentGames?.[0]?.team?.abbreviation || playerData?.team?.abbreviation || ''
  const position = playerData?.position || player?.position || player?.player?.position || ''
  const positionGroup = playerData?.positionGroup || null // QB, RB, WR, TE, DEF, K, UNK
  const displayRows = playerData?.displayRows || null // [{label, value}] from API
  const playerTeamId = player?.player?.team_id || player?.team?.id || recentGames?.[0]?.team?.id
  
  // Injury status
  const injuryStatus = injury?.status?.toUpperCase() || 'IN'
  const injuryReason = injury?.comment || injury?.reason || ''
  const injuryUpdated = injury?.updated_at ? new Date(injury.updated_at) : null
  const statusConfig = STATUS_CONFIG[injuryStatus] || STATUS_CONFIG.IN
  
  // Props data (from game preview or passed in)
  const propLines = useMemo(() => {
    if (!playerProps?.length) return null
    
    // Get player's last name for matching
    const lastName = playerName.toLowerCase().split(' ').pop() || ''
    
    // Find props for this player - try multiple matching strategies
    const myProps = playerProps.find(p => {
      // Match by ID
      if (p.playerId === playerId) return true
      // Match by full name
      if (p.playerName?.toLowerCase() === playerName.toLowerCase()) return true
      // Match by last name
      if (p.playerName?.toLowerCase().includes(lastName)) return true
      return false
    })
    
    if (!myProps?.props) return null
    
    // NBA props
    const nbaProps = {
      pts: myProps.props.find(p => p.type === 'points'),
      reb: myProps.props.find(p => p.type === 'rebounds'),
      ast: myProps.props.find(p => p.type === 'assists'),
      threes: myProps.props.find(p => p.type === 'threes'),
    }
    
    // NFL props - normalize different naming conventions
    const nflProps = {
      pass_yds: myProps.props.find(p => ['passing_yards', 'pass_yds'].includes(p.type)),
      pass_td: myProps.props.find(p => ['passing_tds', 'pass_td', 'passing_touchdowns'].includes(p.type)),
      rush_yds: myProps.props.find(p => ['rushing_yards', 'rush_yds'].includes(p.type)),
      rec_yds: myProps.props.find(p => ['receiving_yards', 'rec_yds'].includes(p.type)),
      receptions: myProps.props.find(p => ['receptions', 'rec'].includes(p.type)),
      anytime_td: myProps.props.find(p => p.type === 'anytime_td'),
    }
    
    return { ...nbaProps, ...nflProps }
  }, [playerProps, playerId, playerName])
  
  // Live game stats (passed from box score)
  const liveStats = player?.pts !== undefined ? {
    pts: player.pts || 0,
    reb: player.reb || 0,
    ast: player.ast || 0,
    min: player.min || 0,
    fgm: player.fgm || 0,
    fga: player.fga || 0,
    pf: player.pf || 0, // fouls
  } : null
  
  // Is this a live game?
  const isLiveGame = gameContext?.status === 'LIVE' || (liveStats !== null && gameContext?.status !== 'FINAL')
  
  // Is this a past/final game?
  const isPastGame = gameContext?.status === 'FINAL'
  
  // Last game stats (most recent from game log)
  const lastGame = recentGames?.[0] || null
  
  // Current prop line for selected stat
  const currentLine = propLines?.[selectedProp]?.line || seasonAvg?.[selectedProp] || 0
  const vsLineStats = computeVsLine(recentGames, currentLine, selectedProp)
  
  // Build AI analysis payload with full player context
  const buildAnalysisPayload = (propType = 'points') => {
    const propLabel = { pts: 'points', reb: 'rebounds', ast: 'assists', threes: '3-pointers' }[propType] || propType
    const rawLine = propLines?.[propType]?.line || seasonAvg?.[propType] || 0
    const line = typeof rawLine === 'string' ? parseFloat(rawLine) : rawLine
    
    // Build the question
    let question = `[PROP ANALYSIS] Analyze ${playerName}'s ${propLabel} prop (line: ${(line || 0).toFixed(1)}).`
    
    if (seasonAvg) {
      const avg = seasonAvg[propType]
      question += ` Season avg: ${typeof avg === 'number' ? avg.toFixed(1) : (avg || 0)}.`
    }
    if (vsLineStats) {
      question += ` Last 5: ${vsLineStats.hitRate} over, avg ${vsLineStats.avg} (${vsLineStats.diffPositive ? '+' : ''}${vsLineStats.diff} vs line).`
    }
    if (injury?.status && injury.status !== 'IN') {
      question += ` Health: ${injuryStatus} (${injuryReason}).`
    }
    
    question += ` Give me: 1) Over/under lean with confidence, 2) Key factors, 3) Risk scenarios.`
    
    // Return rich payload with player context for the agent
    return {
      question,
      playerContext: {
        playerId,
        playerName,
        team: playerTeam,
        propType,
        line,
        seasonAvg: seasonAvg ? {
          pts: seasonAvg.pts,
          reb: seasonAvg.reb,
          ast: seasonAvg.ast,
          fg_pct: seasonAvg.fg_pct,
          min: seasonAvg.min,
        } : null,
        propLines: propLines ? {
          pts: propLines.pts?.line,
          reb: propLines.reb?.line,
          ast: propLines.ast?.line,
          threes: propLines.threes?.line,
        } : null,
        recentGames: recentGames.slice(0, 5).map(g => ({
          pts: g.pts,
          reb: g.reb,
          ast: g.ast,
          fg3m: g.fg3m,
          min: g.min,
        })),
        vsLineStats,
        injury: injury ? { status: injuryStatus, reason: injuryReason } : null,
      },
      gameId: gameContext?.gameId || gameContext?.id,
    }
  }
  
  if (!player) return null
  
  // Team logo URL helper
  const getTeamLogo = (abbr) => {
    if (!abbr) return null
    return `https://cdn.nba.com/logos/nba/${getTeamId(abbr)}/primary/L/logo.svg`
  }
  
  const getTeamId = (abbr) => {
    const teamIds = {
      ATL: 1610612737, BOS: 1610612738, BKN: 1610612751, CHA: 1610612766,
      CHI: 1610612741, CLE: 1610612739, DAL: 1610612742, DEN: 1610612743,
      DET: 1610612765, GSW: 1610612744, HOU: 1610612745, IND: 1610612754,
      LAC: 1610612746, LAL: 1610612747, MEM: 1610612763, MIA: 1610612748,
      MIL: 1610612749, MIN: 1610612750, NOP: 1610612740, NYK: 1610612752,
      OKC: 1610612760, ORL: 1610612753, PHI: 1610612755, PHX: 1610612756,
      POR: 1610612757, SAC: 1610612758, SAS: 1610612759, TOR: 1610612761,
      UTA: 1610612762, WAS: 1610612764
    }
    return teamIds[abbr] || 1610612744
  }
  
  return (
    <div 
      className="fixed inset-0"
      style={{ zIndex: 9999, maxWidth: '430px', margin: '0 auto' }}
    >
      {/* Backdrop - contained to mobile width */}
      <div 
        className="absolute inset-0 bg-black/60" 
        onClick={onClose}
      />
      
      {/* Bottom Sheet */}
      <div 
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl flex flex-col"
        style={{ 
          maxHeight: '85vh',
          animation: 'slideUp 0.2s ease-out forwards',
        }}
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-2">
          <div className="w-9 h-1 bg-gray-300 rounded-full" />
        </div>
        
        {/* Header with Team Logo + Season Avg */}
        <div className="px-4 pt-2 pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {/* Team Logo - league-aware */}
              {teamAbbr && (
                <TeamLogo code={teamAbbr} name={teamAbbr} league={gameContext?.league || 'nba'} size={40} />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-[17px] font-semibold text-gray-900">{playerName}</h3>
                  {ptsRank && ptsRank <= 10 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      ptsRank === 1 ? 'bg-yellow-100 text-yellow-700' : 
                      ptsRank <= 3 ? 'bg-orange-100 text-orange-700' : 
                      'bg-gray-100 text-gray-600'
                    }`}>
                      #{ptsRank} PPG
                    </span>
                  )}
                </div>
                <p className="text-[12px] text-gray-500">
                  {teamAbbr}{position && ` · ${position}`}
                </p>
                {injuryStatus !== 'IN' && (
                  <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded mt-1 ${statusConfig.bg} ${statusConfig.text}`}>
                    {statusConfig.label}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-1 -mr-1 -mt-1">
              <X size={20} className="text-gray-400" />
            </button>
          </div>
          {/* Season Avg - styled row - NFL vs NBA */}
          {/* NFL: Use displayRows from API if available, else fallback to position-based rendering */}
          {playerLeague === 'nfl' && displayRows?.length > 0 ? (
            <div className="flex items-center justify-between mt-2 px-1 py-1.5 bg-gray-50 rounded-lg">
              {displayRows.slice(0, 5).map((row, i) => (
                <React.Fragment key={row.label}>
                  {i > 0 && <div className="w-px h-6 bg-gray-200" />}
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{row.value}</div>
                    <div className="text-[9px] text-gray-400 uppercase">{row.label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          ) : seasonAvg && playerLeague === 'nfl' ? (() => {
            // Fallback: use positionGroup from API or infer from stats
            const nflPos = positionGroup || detectNFLPosition(position, seasonAvg)
            return (
            <div className="flex items-center justify-between mt-2 px-1 py-1.5 bg-gray-50 rounded-lg">
              {/* QB Stats: PASS YDS | PASS TD | INT | RUSH YDS */}
              {nflPos === 'QB' && (
                <>
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.passing_yards || seasonAvg.pass_yds || 0).toFixed(0)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">PASS YDS</div>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.passing_touchdowns || seasonAvg.pass_td || 0).toFixed(1)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">PASS TD</div>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.passing_interceptions || seasonAvg.int || 0).toFixed(1)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">INT</div>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.rushing_yards || seasonAvg.rush_yds || 0).toFixed(0)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">RUSH YDS</div>
                  </div>
                </>
              )}
              {/* RB Stats: RUSH YDS | ATT | TD | REC YDS */}
              {nflPos === 'RB' && (
                <>
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.rushing_yards || seasonAvg.rush_yds || 0).toFixed(0)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">RUSH YDS</div>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.rushing_attempts || seasonAvg.rush_att || 0).toFixed(0)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">ATT</div>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.rushing_touchdowns || seasonAvg.rush_td || 0).toFixed(1)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">TD</div>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.receiving_yards || seasonAvg.rec_yds || 0).toFixed(0)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">REC YDS</div>
                  </div>
                </>
              )}
              {/* WR/TE Stats: REC YDS | REC | TGT | TD */}
              {(nflPos === 'WR' || nflPos === 'TE') && (
                <>
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.receiving_yards || seasonAvg.rec_yds || 0).toFixed(0)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">REC YDS</div>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.receptions || seasonAvg.rec || 0).toFixed(0)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">REC</div>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.targets || seasonAvg.tgt || 0).toFixed(0)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">TGT</div>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="flex-1 text-center">
                    <div className="text-[14px] font-semibold text-gray-900">{(seasonAvg.receiving_touchdowns || seasonAvg.rec_td || 0).toFixed(1)}</div>
                    <div className="text-[9px] text-gray-400 uppercase">TD</div>
                  </div>
                </>
              )}
              {/* Fallback if no stats */}
              {!(seasonAvg.passing_yards > 0) && !(seasonAvg.rushing_yards > 5) && !(seasonAvg.receptions > 0) && 
               !(seasonAvg.pass_yds > 0) && !(seasonAvg.rush_yds > 5) && !(seasonAvg.rec > 0) && (
                <div className="flex-1 text-center text-gray-400 text-[11px]">No stats available</div>
              )}
            </div>
            )
          })() : seasonAvg && (
            <div className="flex items-center justify-between mt-2 px-1 py-1.5 bg-gray-50 rounded-lg">
              <div className="flex-1 text-center">
                <div className="text-[14px] font-semibold text-gray-900">{seasonAvg.pts?.toFixed(1)}</div>
                <div className="text-[9px] text-gray-400 uppercase">PPG</div>
              </div>
              <div className="w-px h-6 bg-gray-200" />
              <div className="flex-1 text-center">
                <div className="text-[14px] font-semibold text-gray-900">{seasonAvg.reb?.toFixed(1)}</div>
                <div className="text-[9px] text-gray-400 uppercase">RPG</div>
              </div>
              <div className="w-px h-6 bg-gray-200" />
              <div className="flex-1 text-center">
                <div className="text-[14px] font-semibold text-gray-900">{seasonAvg.ast?.toFixed(1)}</div>
                <div className="text-[9px] text-gray-400 uppercase">APG</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Tab Switcher */}
        <div className="px-4 pb-2">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 py-1.5 text-[12px] font-medium rounded-md transition-all ${
                activeTab === 'profile' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500'
              }`}
            >
              Props
            </button>
            <button
              onClick={() => setActiveTab('gamelog')}
              className={`flex-1 py-1.5 text-[12px] font-medium rounded-md transition-all ${
                activeTab === 'gamelog' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-500'
              }`}
            >
              Game Log
            </button>
          </div>
        </div>
        
        {/* Scrollable Content - Swipeable */}
        <div 
          className="flex-1 overflow-y-auto" 
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-gray-300" />
            </div>
          ) : activeTab === 'gamelog' ? (
            /* GAME LOG TAB */
            <GameLogTab 
              recentGames={recentGames} 
              playerTeamId={playerTeamId}
              playerName={playerName}
              onAskAI={onAskAI}
              onClose={onClose}
              league={playerLeague}
            />
          ) : (
            <div className="px-5 py-4 space-y-5">
              
              {/* NFL SEASON AVERAGES - Position-specific */}
              {playerLeague === 'nfl' && seasonAvg && (
                <div className="mb-4">
                  <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-2">Season Averages</p>
                  {/* QB Stats */}
                  {seasonAvg.passing_yards > 0 && (
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {[
                        { label: 'PASS', value: seasonAvg.passing_yards?.toFixed(0) },
                        { label: 'TD', value: seasonAvg.passing_touchdowns?.toFixed(1) },
                        { label: 'INT', value: seasonAvg.passing_interceptions?.toFixed(1) },
                        { label: 'RTG', value: seasonAvg.qb_rating?.toFixed(1) },
                      ].map(({ label, value }) => (
                        <div key={label} className="p-2 rounded-lg text-center bg-gray-50">
                          <p className="text-[10px] text-gray-400">{label}</p>
                          <p className="text-lg font-bold text-gray-800">{value}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* RB Stats */}
                  {seasonAvg.rushing_yards > 5 && (
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {[
                        { label: 'RUSH', value: seasonAvg.rushing_yards?.toFixed(0) },
                        { label: 'ATT', value: seasonAvg.rushing_attempts?.toFixed(0) },
                        { label: 'TD', value: seasonAvg.rushing_touchdowns?.toFixed(1) },
                        { label: 'YPC', value: seasonAvg.rushing_attempts > 0 ? (seasonAvg.rushing_yards / seasonAvg.rushing_attempts).toFixed(1) : '0' },
                      ].map(({ label, value }) => (
                        <div key={label} className="p-2 rounded-lg text-center bg-gray-50">
                          <p className="text-[10px] text-gray-400">{label}</p>
                          <p className="text-lg font-bold text-gray-800">{value}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* WR/TE Stats */}
                  {seasonAvg.receptions > 0 && (
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { label: 'REC', value: seasonAvg.receptions?.toFixed(0) },
                        { label: 'YDS', value: seasonAvg.receiving_yards?.toFixed(0) },
                        { label: 'TD', value: seasonAvg.receiving_touchdowns?.toFixed(1) },
                        { label: 'TGT', value: seasonAvg.targets?.toFixed(0) },
                      ].map(({ label, value }) => (
                        <div key={label} className="p-2 rounded-lg text-center bg-gray-50">
                          <p className="text-[10px] text-gray-400">{label}</p>
                          <p className="text-lg font-bold text-gray-800">{value}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* LAST GAME - Show for past/final games (NBA) */}
              {playerLeague !== 'nfl' && isPastGame && liveStats && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                      {gameContext?.date ? new Date(gameContext.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Last Game'}
                    </p>
                    <p className="text-[10px] text-gray-400">{gameContext?.title || ''}</p>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: 'PTS', value: liveStats.pts },
                      { label: 'REB', value: liveStats.reb },
                      { label: 'AST', value: liveStats.ast },
                      { label: 'MIN', value: liveStats.min },
                    ].map(({ label, value }) => (
                      <div key={label} className="p-2 rounded-lg text-center bg-gray-50">
                        <p className="text-[10px] text-gray-400">{label}</p>
                        <p className="text-lg font-bold text-gray-800">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* LIVE PROP TRACKER - Compact with pace info */}
              {isLiveGame && liveStats && propLines && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-medium text-green-600 uppercase tracking-wide">🔴 Live</p>
                    <p className="text-[10px] text-gray-400">{liveStats.min || 0} min played</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'pts', label: 'PTS', current: liveStats.pts, line: propLines.pts?.line },
                      { key: 'reb', label: 'REB', current: liveStats.reb, line: propLines.reb?.line },
                      { key: 'ast', label: 'AST', current: liveStats.ast, line: propLines.ast?.line },
                    ].map(({ key, label, current, line }) => {
                      if (!line) return null
                      const lineVal = parseFloat(line)
                      const needed = lineVal - current
                      const hit = needed <= 0
                      const close = needed > 0 && needed <= 3
                      
                      return (
                        <div key={key} className={`p-2.5 rounded-xl text-center border ${
                          hit ? 'border-green-200 bg-green-50' : close ? 'border-orange-200 bg-orange-50' : 'border-gray-100'
                        }`}>
                          <div className="flex items-baseline justify-center gap-1">
                            <p className={`text-xl font-bold ${hit ? 'text-green-600' : close ? 'text-orange-500' : 'text-gray-800'}`}>
                              {current}
                            </p>
                            <p className="text-[10px] text-gray-400">/{lineVal}</p>
                          </div>
                          <p className={`text-[9px] font-medium mt-0.5 ${hit ? 'text-green-600' : close ? 'text-orange-500' : 'text-gray-400'}`}>
                            {hit ? '✓' : `${needed.toFixed(1)} left`}
                          </p>
                        </div>
                      )
                    }).filter(Boolean)}
                  </div>
                  {/* Fouls warning */}
                  {liveStats.pf >= 4 && (
                    <p className="text-[10px] text-orange-600 font-medium mt-2 text-center">
                      ⚠️ {liveStats.pf} fouls
                    </p>
                  )}
                </div>
              )}
              
              {/* TONIGHT'S PROPS - Only show if NOT live and we have props */}
              {propLines && !isLiveGame && !liveStats ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Tonight's Props</p>
                    {/* Book toggle */}
                    <div className="flex items-center gap-0.5 bg-gray-100 rounded-full p-0.5">
                      <button
                        onClick={() => setSelectedBook('dk')}
                        className={`text-[9px] font-medium px-2.5 py-1 rounded-full transition ${
                          selectedBook === 'dk' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400'
                        }`}
                      >
                        DraftKings
                      </button>
                      <button
                        onClick={() => setSelectedBook('fd')}
                        className={`text-[9px] font-medium px-2.5 py-1 rounded-full transition ${
                          selectedBook === 'fd' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400'
                        }`}
                      >
                        FanDuel
                      </button>
                    </div>
                  </div>
                  
                  {/* Props Grid - Show line + over/under odds - NFL vs NBA */}
                  <div className="space-y-2">
                    {(playerLeague === 'nfl' ? (() => {
                      const nflPos = detectNFLPosition(position, seasonAvg)
                      // Position-specific props
                      if (nflPos === 'QB') return [
                        { key: 'pass_yds', label: 'Pass Yards' },
                        { key: 'pass_td', label: 'Pass TDs' },
                        { key: 'rush_yds', label: 'Rush Yards' },
                        { key: 'anytime_td', label: 'Anytime TD' },
                      ]
                      if (nflPos === 'RB') return [
                        { key: 'rush_yds', label: 'Rush Yards' },
                        { key: 'receptions', label: 'Receptions' },
                        { key: 'rec_yds', label: 'Rec Yards' },
                        { key: 'anytime_td', label: 'Anytime TD' },
                      ]
                      // WR/TE
                      return [
                        { key: 'receptions', label: 'Receptions' },
                        { key: 'rec_yds', label: 'Rec Yards' },
                        { key: 'anytime_td', label: 'Anytime TD' },
                      ]
                    })() : [
                      { key: 'pts', label: 'Points' },
                      { key: 'reb', label: 'Rebounds' },
                      { key: 'ast', label: 'Assists' },
                      { key: 'threes', label: '3-Pointers' },
                    ]).map(({ key, label }) => {
                      const prop = propLines?.[key]
                      if (!prop?.line) return null
                      
                      const lineValue = parseFloat(prop.line)
                      const stats = computeVsLine(recentGames, lineValue, key === 'threes' ? 'fg3m' : key)
                      const isSelected = selectedProp === key
                      const overOdds = prop?.overOdds || -110
                      const underOdds = prop?.underOdds || -110
                      
                      return (
                        <button
                          key={key}
                          onClick={() => setSelectedProp(key)}
                          className={`w-full flex items-center justify-between p-3 rounded-xl transition-all border ${
                            isSelected 
                              ? 'bg-gray-50 border-gray-200' 
                              : 'bg-white border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="text-[11px] text-gray-500">{label}</p>
                              <p className="text-lg font-semibold text-gray-900">{lineValue.toFixed(1)}</p>
                            </div>
                          </div>
                          
                          {/* Over/Under with odds */}
                          <div className="flex items-center gap-2">
                            <div className="text-center px-2 py-1 bg-green-50 rounded-lg">
                              <p className="text-[9px] text-green-600 font-medium">Over</p>
                              <p className="text-[11px] font-semibold text-green-700">
                                {overOdds > 0 ? '+' : ''}{overOdds}
                              </p>
                            </div>
                            <div className="text-center px-2 py-1 bg-red-50 rounded-lg">
                              <p className="text-[9px] text-red-500 font-medium">Under</p>
                              <p className="text-[11px] font-semibold text-red-600">
                                {underOdds > 0 ? '+' : ''}{underOdds}
                              </p>
                            </div>
                            {stats && (
                              <div className={`text-center px-2 py-1 rounded-lg ${
                                stats.diffPositive ? 'bg-green-50' : 'bg-red-50'
                              }`}>
                                <p className="text-[9px] text-gray-500">L5</p>
                                <p className={`text-[11px] font-semibold ${
                                  stats.diffPositive ? 'text-green-600' : 'text-red-500'
                                }`}>
                                  {stats.hitRate}
                                </p>
                              </div>
                            )}
                          </div>
                        </button>
                      )
                    }).filter(Boolean)}
                  </div>
                </div>
              ) : seasonAvg && !isLiveGame && !liveStats ? (
                /* No props - show season avg as reference line (only pre-game) */
                <div>
                  <p className="text-[11px] text-gray-400 mb-3">No props posted yet. Using season avg as reference:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'pts', label: 'PTS', avg: seasonAvg.pts },
                      { key: 'reb', label: 'REB', avg: seasonAvg.reb },
                      { key: 'ast', label: 'AST', avg: seasonAvg.ast },
                    ].map(({ key, label, avg }) => {
                      if (!avg) return null
                      const lineVal = avg
                      const stats = computeVsLine(recentGames, lineVal, key)
                      const isSelected = selectedProp === key
                      
                      return (
                        <div
                          key={key}
                          className="p-3 rounded-xl text-center border border-gray-100"
                        >
                          <p className="text-[10px] text-gray-400 mb-1">{label}</p>
                          <p className="text-lg font-semibold text-gray-900">{lineVal.toFixed(1)}</p>
                          {stats && (
                            <p className={`text-[10px] font-medium mt-1 ${
                              stats.diffPositive ? 'text-green-600' : 'text-red-500'
                            }`}>
                              {stats.hitRate} over
                            </p>
                          )}
                        </div>
                      )
                    }).filter(Boolean)}
                  </div>
                </div>
              ) : null}
              
              {/* Recent Games - Format: actual/line (green if hit, red if miss) - NFL vs NBA */}
              {recentGames.length > 0 && (
                <div>
                  {/* Header - NFL vs NBA columns */}
                  <div className="flex items-center justify-between mb-2 px-3">
                    <span className="text-[10px] text-gray-400 uppercase font-medium w-14">Last 5</span>
                    {playerLeague === 'nfl' ? (() => {
                      const nflPos = detectNFLPosition(position, seasonAvg)
                      if (nflPos === 'QB') return (
                        <div className="flex items-center text-[10px] text-gray-400 font-medium">
                          <span className="w-14 text-center">PASS</span>
                          <span className="w-14 text-center">TD</span>
                          <span className="w-14 text-center">INT</span>
                          <span className="w-14 text-center">RUSH</span>
                        </div>
                      )
                      if (nflPos === 'RB') return (
                        <div className="flex items-center text-[10px] text-gray-400 font-medium">
                          <span className="w-14 text-center">RUSH</span>
                          <span className="w-14 text-center">ATT</span>
                          <span className="w-14 text-center">REC</span>
                          <span className="w-14 text-center">TD</span>
                        </div>
                      )
                      // WR/TE
                      return (
                        <div className="flex items-center text-[10px] text-gray-400 font-medium">
                          <span className="w-14 text-center">REC</span>
                          <span className="w-14 text-center">YDS</span>
                          <span className="w-14 text-center">TGT</span>
                          <span className="w-14 text-center">TD</span>
                        </div>
                      )
                    })() : (
                      <div className="flex items-center text-[10px] text-gray-400 font-medium">
                        <span className="w-14 text-center">PTS</span>
                        <span className="w-14 text-center">REB</span>
                        <span className="w-14 text-center">AST</span>
                        <span className="w-14 text-center">3PM</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    {recentGames.slice(0, 5).map((game, i) => {
                      const { opp, date } = formatOpponent(game, playerTeamId)
                      
                      // NFL stats
                      const passYds = game?.pass_yds ?? game?.passing_yards ?? 0
                      const rushYds = game?.rush_yds ?? game?.rushing_yards ?? 0
                      const recYds = game?.rec_yds ?? game?.receiving_yards ?? 0
                      const totalTd = game?.total_td ?? ((game?.pass_td ?? 0) + (game?.rush_td ?? 0) + (game?.rec_td ?? 0))
                      
                      // NBA stats
                      const pts = game?.pts ?? 0
                      const reb = game?.reb ?? 0
                      const ast = game?.ast ?? 0
                      const fg3m = game?.fg3m ?? 0
                      
                      // Lines (prop or season avg) - NFL vs NBA
                      const ptsLine = propLines?.pts?.line ? parseFloat(propLines.pts.line) : seasonAvg?.pts
                      const rebLine = propLines?.reb?.line ? parseFloat(propLines.reb.line) : seasonAvg?.reb
                      const astLine = propLines?.ast?.line ? parseFloat(propLines.ast.line) : seasonAvg?.ast
                      const threesLine = propLines?.threes?.line ? parseFloat(propLines.threes.line) : null
                      
                      // NFL lines
                      const passLine = propLines?.pass_yds?.line ? parseFloat(propLines.pass_yds.line) : null
                      const rushLine = propLines?.rush_yds?.line ? parseFloat(propLines.rush_yds.line) : null
                      const recLine = propLines?.rec_yds?.line ? parseFloat(propLines.rec_yds.line) : null
                      
                      // Helper to render stat/line
                      const StatCell = ({ val, line }) => {
                        if (!line) return <span className="text-gray-700">{val}</span>
                        let lineNum = typeof line === 'string' ? parseFloat(line) : line
                        // Round to nearest .5 (17.6 -> 17.5, 18.2 -> 18)
                        lineNum = Math.round(lineNum * 2) / 2
                        const hit = val >= lineNum
                        // Show .5 only if it's a half, otherwise whole number
                        const lineStr = lineNum % 1 === 0.5 ? lineNum.toFixed(1) : lineNum.toFixed(0)
                        return (
                          <span className={hit ? 'text-green-600' : 'text-red-500'}>
                            {val}<span className="text-gray-400">/{lineStr}</span>
                          </span>
                        )
                      }
                      
                      return (
                        <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                          <div className="w-14">
                            <p className="text-[11px] font-medium text-gray-700">{game?.opponent || opp}</p>
                            <p className="text-[9px] text-gray-400">{game?.date ? new Date(game.date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : date}</p>
                          </div>
                          
                          {/* Stats: actual/line format - NFL vs NBA */}
                          {playerLeague === 'nfl' ? (() => {
                            const nflPos = detectNFLPosition(position, seasonAvg)
                            const passTd = game?.pass_td ?? game?.passing_touchdowns ?? 0
                            const intVal = game?.int ?? game?.interceptions ?? 0
                            const rushAtt = game?.rush_att ?? game?.rushing_attempts ?? 0
                            const recs = game?.rec ?? game?.receptions ?? 0
                            const targets = game?.tgt ?? game?.targets ?? 0
                            const recTd = game?.rec_td ?? game?.receiving_touchdowns ?? 0
                            const rushTd = game?.rush_td ?? game?.rushing_touchdowns ?? 0
                            
                            if (nflPos === 'QB') return (
                              <div className="flex items-center text-[12px] font-medium">
                                <span className="w-14 text-center"><StatCell val={passYds} line={passLine} /></span>
                                <span className="w-14 text-center"><span className={passTd > 0 ? 'text-pink-600' : 'text-gray-700'}>{passTd}</span></span>
                                <span className="w-14 text-center"><span className={intVal > 0 ? 'text-red-500' : 'text-gray-700'}>{intVal}</span></span>
                                <span className="w-14 text-center"><StatCell val={rushYds} line={rushLine} /></span>
                              </div>
                            )
                            if (nflPos === 'RB') return (
                              <div className="flex items-center text-[12px] font-medium">
                                <span className="w-14 text-center"><StatCell val={rushYds} line={rushLine} /></span>
                                <span className="w-14 text-center"><span className="text-gray-700">{rushAtt}</span></span>
                                <span className="w-14 text-center"><StatCell val={recYds} line={recLine} /></span>
                                <span className="w-14 text-center"><span className={totalTd > 0 ? 'text-pink-600' : 'text-gray-700'}>{totalTd}</span></span>
                              </div>
                            )
                            // WR/TE
                            return (
                              <div className="flex items-center text-[12px] font-medium">
                                <span className="w-14 text-center"><span className="text-gray-700">{recs}</span></span>
                                <span className="w-14 text-center"><StatCell val={recYds} line={recLine} /></span>
                                <span className="w-14 text-center"><span className="text-gray-700">{targets}</span></span>
                                <span className="w-14 text-center"><span className={recTd > 0 ? 'text-pink-600' : 'text-gray-700'}>{recTd}</span></span>
                              </div>
                            )
                          })() : (
                            <div className="flex items-center text-[12px] font-medium">
                              <span className="w-14 text-center">
                                <StatCell val={pts} line={ptsLine} />
                              </span>
                              <span className="w-14 text-center">
                                <StatCell val={reb} line={rebLine} />
                              </span>
                              <span className="w-14 text-center">
                                <StatCell val={ast} line={astLine} />
                              </span>
                              <span className="w-14 text-center">
                                {threesLine ? <StatCell val={fg3m} line={threesLine} /> : <span className="text-gray-700">{fg3m}</span>}
                              </span>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              
              {/* No data state */}
              {!seasonAvg && recentGames.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-sm text-gray-400">No stats available</p>
                </div>
              )}
            </div>
          )}
        </div>
        
      </div>
      
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
